import React from 'react';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faQuestionCircle, faSearch, faSignIn } from '@fortawesome/free-solid-svg-icons';
import styled, { css } from 'styled-components';

import HeaderLogo from 'components/HeaderLogo/HeaderLogo';
import IconButton from 'components/IconButton/IconButton';
import HamburgerMenuToggle from 'components/Layout/Header/components/HamburgerMenuToggle';

import { LinkI } from 'interfaces/content-stack/contentStack.types';

import { getModularBlockLinkData } from 'utils/modularBlockUtils';

interface BarProps {
  $isSidebarOpen: boolean;
}

const MobileNavWrapper = styled.div<{ $alertOpen: boolean; $alertHeight: number }>`
  ${({ theme, $alertOpen, $alertHeight }) => css`
    position: sticky;
    top: ${$alertOpen ? `${$alertHeight}px` : '0'};
    width: 100%;
    background-color: ${theme.colors.white};
    display: none;
    align-items: center;
    justify-content: space-between;
    padding-right: 1rem;
    gap: 2.5rem;
    height: 3.75rem;
    box-shadow: ${theme.colors.boxShadowGray} 0 0.0625rem 0.125rem 0;
    z-index: 1000;

    @media (max-width: ${theme.breakpoints.lg}) {
      display: flex;
    }
  `}
`;

const RawButton = styled.button<BarProps>`
  ${({ theme }) => css`
    width: 3.75rem;
    height: 3.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 0.0625rem solid ${theme.colors.borderLightGray};
    cursor: pointer;
    background: unset;
    border: unset;
  `}
`;

const NavItemContainerLeft = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 2.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    gap: 1rem;
  }
`;

const StyledIconButton = styled(IconButton)(
  ({ theme }) => css`
    color: ${theme.colors.primaryRed};
    font-size: 1.15rem;
  `
);

const NavItemContainerRight = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    gap: 1rem;
  }
`;

export interface MobileNavProps {
  data: {
    linkIcon: string;
    link: LinkI[];
  }[];
  toggleSidebar: () => void;
  isSidebarOpen: boolean;
  setShowSearchBar: (show: boolean) => void;
  alertOpen: boolean;
  alertHeight: number;
}

const MobileNav: React.FC<MobileNavProps> = ({
  data,
  toggleSidebar,
  isSidebarOpen,
  setShowSearchBar,
  alertOpen,
  alertHeight,
}) => {
  const menuIcons: { [item: string]: IconDefinition } = {
    question: faQuestionCircle,
    signin: faSignIn,
  };

  return (
    <MobileNavWrapper $alertOpen={alertOpen} $alertHeight={alertHeight}>
      <NavItemContainerLeft>
        <RawButton
          data-testid='toggleSidebar'
          type='button'
          aria-label='toggleSidebar'
          onClick={toggleSidebar}
          $isSidebarOpen={isSidebarOpen}
        >
          <HamburgerMenuToggle isSidebarOpen={isSidebarOpen} />
        </RawButton>
        <HeaderLogo />
      </NavItemContainerLeft>

      <NavItemContainerRight>
        {data?.map((item, index) => {
          const Icon = menuIcons[item.linkIcon];
          const { link, target } = getModularBlockLinkData(item.link);
          return (
            <IconButton
              aria-label='Mobile nav'
              key={index}
              to={link}
              target={target}
              icon={Icon}
              data-testid='mobile-nav-button'
            />
          );
        })}
        <StyledIconButton
          aria-label='Search'
          onClick={() => setShowSearchBar(true)}
          icon={faSearch}
          data-testid='mobile-search-button'
        />
      </NavItemContainerRight>
    </MobileNavWrapper>
  );
};

export default MobileNav;
